/**
 * Start from scratch: three ways to a brief — write it, upload material (Lykke reads it and writes the brief),
 * or a strategy call with Lykke (she shapes the brief on screen as you talk, reads the shape back, and on your
 * yes starts building). "Build it" creates the deck with the confirmed brief and every page is written at once;
 * "Plan the pages first" keeps the review step (planned pages you approve before anything is written).
 */
import { useEffect, useRef, useState } from 'react';
import { NumberField } from '@/cowork/shared/NumberField';
import { Loader2, Mic, MicOff, Paperclip, PenLine, PhoneOff, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { VisibilityControl, type VisibilityValue } from '@/cowork/omniad/components/VisibilityControl';
import { RealtimeCall, type RealtimeTool } from '@/cowork/proposals/realtimeCall';
import { AGENT } from '@/cowork/proposals/ProposalBuilder';

export interface ScratchStart { title: string; brief: string; pages: number; autoBuild: boolean; visibility: VisibilityValue }
const MATERIAL_ACCEPT = '.pdf,.pptx,.docx,.txt,.md,.csv,image/png,image/jpeg,image/webp,image/gif';
const labelCls = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#667085]';
const inputCls = 'w-full rounded-lg border border-[#d0d5dd] px-3 py-2 text-[13px] text-[#101828] focus:border-[#be43ff] focus:outline-none';
const titleFrom = (brief: string) => brief.split(/[.\n]/)[0].trim().slice(0, 60) || 'Untitled report';

type CallState = { phase: 'connecting' | 'listening' | 'speaking' | 'working' | 'error'; caption: string; heard: string; error?: string; muted: boolean };

export function StartFromScratch({ brandId, onStart }: { brandId?: string; onStart: (s: ScratchStart) => Promise<void> }) {
  const [brief, setBrief] = useState('');
  const [title, setTitle] = useState('');
  const [pages, setPages] = useState(8);
  const [vis, setVis] = useState<VisibilityValue>({ visibility: 'team', allowedUserIds: [] });
  const [busy, setBusy] = useState<'build' | 'plan' | 'material' | null>(null);
  const [material, setMaterial] = useState<{ sources: string[]; notes: string[] } | null>(null);
  const [call, setCall] = useState<CallState | null>(null);
  const [flash, setFlash] = useState(false);
  const callRef = useRef<RealtimeCall | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const briefRef = useRef<HTMLTextAreaElement>(null);
  const state = useRef({ brief, title, pages, vis });
  state.current = { brief, title, pages, vis };
  useEffect(() => () => { callRef.current?.end(); callRef.current = null; }, []);

  const start = async (autoBuild: boolean, from = state.current) => {
    const b = from.brief.trim();
    if (!b) { toast.message('Tell Lykke what the deck is about first.'); briefRef.current?.focus(); return; }
    setBusy(autoBuild ? 'build' : 'plan');
    try { await onStart({ title: from.title.trim() || titleFrom(b), brief: `${b}${/\bpages?\b/i.test(b) ? '' : ` About ${from.pages} pages.`}`, pages: from.pages, autoBuild, visibility: from.vis }); }
    catch (e) { toast.error(`Could not start the deck: ${(e as Error).message}`); }
    finally { setBusy(null); }
  };

  const readMaterial = async (files: File[]) => {
    if (!files.length) return;
    setBusy('material');
    try {
      const r = await coworkApi.presentations.briefFromMaterial(files, { text: brief.trim() || undefined, brandId });
      setBrief(r.brief); setTitle(r.title); setPages(r.pages); setMaterial({ sources: r.sources, notes: r.notes });
      setFlash(true); setTimeout(() => setFlash(false), 1200);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(null); }
  };

  const endCall = () => { callRef.current?.end(); callRef.current = null; setCall(null); };
  const strategize = async () => {
    if (callRef.current) return;
    setCall({ phase: 'connecting', caption: '', heard: '', muted: false });
    const patch = (p: Partial<CallState>) => setCall((c) => (c ? { ...c, ...p } : c));
    try {
      const secret = await coworkApi.presentations.strategizeSecret({ brandId, language: navigator.language, brief: state.current.brief.trim() || undefined });
      const tools: RealtimeTool[] = [
        { name: 'update_brief', description: 'Write the whole brief so far into the brief box on their screen (not a delta).', silent: true,
          parameters: { type: 'object', properties: { title: { type: 'string' }, brief: { type: 'string' }, pages: { type: 'integer' } }, required: ['brief'] },
          execute: async (a) => {
            if (typeof a.brief === 'string' && a.brief.trim()) setBrief(a.brief.trim());
            if (typeof a.title === 'string' && a.title.trim()) setTitle(a.title.trim());
            if (Number(a.pages) >= 2) setPages(Math.max(2, Math.min(40, Math.round(Number(a.pages)))));
            setFlash(true); setTimeout(() => setFlash(false), 900);
            return { ok: true };
          } },
        { name: 'start_building', description: 'Create the deck from the brief on screen and write every page. Only after they clearly said yes to building it.',
          parameters: { type: 'object', properties: {} },
          execute: async () => {
            if (!state.current.brief.trim()) return { ok: false, error: 'The brief is empty — call update_brief first.' };
            patch({ phase: 'working' });
            // Let her finish her line, then hand over to the editor, where she keeps going.
            const snapshot = { ...state.current };
            setTimeout(() => { const c = callRef.current; if (c) c.afterAudio(() => { endCall(); void start(true, snapshot); }); else void start(true, snapshot); }, 300);
            return { ok: true, note: 'Say one short line: you are building it now and the deck is opening.' };
          } },
      ];
      const rc = new RealtimeCall(tools, {
        onStatus: (st) => { if (st === 'speaking') patch({ phase: 'speaking' }); else if (st === 'listening' || st === 'connected') patch({ phase: 'listening' }); else if (st === 'tool') patch({ phase: 'working' }); else if (st === 'error') patch({ phase: 'error' }); },
        onTranscript: (role, text, final) => { if (role === 'user') patch({ heard: text }); else patch({ caption: text, ...(final ? {} : {}) }); },
        onAudio: (playing) => patch({ phase: playing ? 'speaking' : 'listening' }),
        onError: (m) => patch({ phase: 'error', error: m }),
      });
      callRef.current = rc;
      await rc.connect(secret.clientSecret, secret.model, { instructions: secret.instructions, voice: secret.voice, opening: 'Open the call as your instructions say.' });
    } catch (e) {
      callRef.current?.end(); callRef.current = null;
      setCall({ phase: 'error', caption: '', heard: '', muted: false, error: (e as Error).message });
    }
  };
  const toggleMute = () => setCall((c) => { if (!c) return c; callRef.current?.setMuted(!c.muted); return { ...c, muted: !c.muted }; });

  const way = 'flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition';
  const phaseLabel = call ? ({ connecting: 'Calling Lykke…', listening: 'Listening', speaking: 'Lykke is speaking', working: 'Working on it…', error: 'Call ended' } as const)[call.phase] : '';
  return (
    <div className="grid flex-1 gap-6 overflow-y-auto p-6 md:grid-cols-[1fr_17rem]">
      <div className="min-w-0 space-y-4">
        <div className="grid gap-2.5 sm:grid-cols-3">
          <button type="button" onClick={() => briefRef.current?.focus()} className={`${way} border-[#eaecf0] hover:border-[#be43ff] hover:bg-[#fcf7ff]`}>
            <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-[#be43ff]" />
            <span><span className="block text-[13px] font-semibold text-[#101828]">Write it</span><span className="block text-[11px] text-[#667085]">A few lines on what the deck is for</span></span>
          </button>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy === 'material'} className={`${way} border-[#eaecf0] hover:border-[#be43ff] hover:bg-[#fcf7ff] disabled:opacity-60`}>
            {busy === 'material' ? <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#be43ff]" /> : <Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-[#be43ff]" />}
            <span><span className="block text-[13px] font-semibold text-[#101828]">{busy === 'material' ? 'Lykke is reading…' : 'Upload material'}</span><span className="block text-[11px] text-[#667085]">A brief, notes or an old deck: PDF, PowerPoint, Word or pictures</span></span>
            <input ref={fileRef} type="file" multiple accept={MATERIAL_ACCEPT} className="hidden" onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ''; void readMaterial(f); }} />
          </button>
          <button type="button" onClick={() => void strategize()} disabled={!!call} className={`${way} ${call ? 'border-[#be43ff] bg-[#fcf7ff]' : 'border-[#eaecf0] hover:border-[#be43ff] hover:bg-[#fcf7ff]'}`}>
            <img src={AGENT.photo} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
            <span><span className="block text-[13px] font-semibold text-[#101828]">Strategize with Lykke</span><span className="block text-[11px] text-[#667085]">Talk it through; she writes the brief and builds it</span></span>
          </button>
        </div>

        {call && (
          <div className="flex items-center gap-3 rounded-xl border border-[#e9d7fe] bg-[#fcf7ff] px-3.5 py-3" role="status" aria-live="polite">
            <div className={`relative h-12 w-12 shrink-0 overflow-hidden rounded-full ${call.phase === 'speaking' ? 'ring-2 ring-[#be43ff] ring-offset-2' : ''}`}>
              <video src={AGENT.video} poster={AGENT.photo} autoPlay loop muted playsInline className="h-full w-full object-cover" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#7a1fbf]">{phaseLabel}</div>
              <div className="truncate text-[13px] text-[#101828]">{call.error || call.caption || (call.phase === 'connecting' ? 'Allow the microphone if your browser asks.' : '…')}</div>
              {call.heard && <div className="truncate text-[11px] text-[#667085]">You: {call.heard}</div>}
            </div>
            {call.phase !== 'error' && <button type="button" onClick={toggleMute} title={call.muted ? 'Unmute' : 'Mute'} className="rounded-full border border-[#d0d5dd] bg-white p-2 text-[#344054] hover:bg-[#f9fafb]">{call.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}</button>}
            <button type="button" onClick={endCall} title="End the call" className="rounded-full bg-[#d92d20] p-2 text-white hover:bg-[#b42318]">{call.phase === 'error' ? <X className="h-4 w-4" /> : <PhoneOff className="h-4 w-4" />}</button>
          </div>
        )}

        <div>
          <label className={labelCls} htmlFor="scratch-brief">What should this deck be?</label>
          <textarea id="scratch-brief" ref={briefRef} value={brief} onChange={(e) => setBrief(e.target.value)} rows={9} autoFocus
            placeholder={'A year plan for Merrell: where we are, the creator programme by season, how paid amplifies it, the numbers we commit to, next steps.'}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void start(true); }}
            className={`${inputCls} resize-y leading-relaxed transition-shadow ${flash ? 'shadow-[0_0_0_3px_rgba(190,67,255,0.25)]' : ''}`} />
          {material && (
            <div className="mt-1.5 text-[11px] text-[#667085]">
              Lykke wrote this from {material.sources.join(', ') || 'your text'} — change anything.
              {material.notes.length > 0 && <span className="text-[#b54708]"> Could not read: {material.notes.join('; ')}.</span>}
            </div>
          )}
          {!material && <div className="mt-1 text-[11px] text-[#98a2b3]">⌘↵ builds it. You can change every page afterwards, by typing or by talking to Lykke.</div>}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className={labelCls} htmlFor="scratch-title">Title</label>
          <input id="scratch-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={brief.trim() ? titleFrom(brief) : 'Untitled report'} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="scratch-pages">About how many pages</label>
          <NumberField id="scratch-pages" min={2} max={40} value={pages} onChange={(n) => setPages(n ?? 2)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Who can see it</label>
          <VisibilityControl value={vis} onChange={setVis} />
        </div>
        <button onClick={() => void start(true)} disabled={!!busy || !brief.trim()} className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#be43ff] px-3.5 py-2.5 text-[13px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-50">
          {busy === 'build' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Build it
        </button>
        <button onClick={() => void start(false)} disabled={!!busy || !brief.trim()} className="w-full rounded-lg border border-[#d0d5dd] px-3.5 py-2 text-[12.5px] font-medium text-[#344054] hover:bg-[#f9fafb] disabled:opacity-50">
          {busy === 'plan' ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Plan the pages first'}
        </button>
        <div className="text-[11px] leading-snug text-[#98a2b3]"><b className="text-[#667085]">Build it</b> writes every page now. <b className="text-[#667085]">Plan the pages first</b> shows the planned pages to approve before anything is written.</div>
      </div>
    </div>
  );
}
